import { useCallback } from 'react';
import qs from 'qs';
import { useStateResetOn } from './useStateResetOn';

interface CursorPaginationState {
  cursors: (string | null)[];
  currentIndex: number;
}

interface UseCursorPaginationOptions {
  /**
   * Serialized form of every query input the cursors are bound to — sort, filters, page size,
   * search. Build it with {@link buildCursorResetKey}. When it changes, the cursor stack is
   * discarded and pagination restarts at page 1.
   */
  resetKey: string;
}

interface UseCursorPaginationReturn {
  currentCursor: string | null;
  currentPage: number;
  canGoBack: boolean;
  canGoForward: boolean;
  isNextCursorCached: (nextCursor: string | null) => boolean;
  goToNextPage: (nextCursor: string | null) => void;
  goToPreviousPage: () => void;
}

const INITIAL_STATE: CursorPaginationState = {
  cursors: [null], // start with null for first page
  currentIndex: 0,
};

const alphabetically = (a: string, b: string) => a.localeCompare(b);

/**
 * Serializes query inputs into a stable cursor reset key.
 *
 * Keys are sorted at every level so that two semantically identical states — one parsed back out
 * of the URL, one assembled by a filter merge — produce the same string. Without that, harmless
 * key reordering would look like a change and bounce the user back to page 1.
 *
 * @param queryInputs Everything the cursors depend on. Passing a whole url state object is fine;
 * including a field that does not affect the query only costs an occasional extra reset, whereas
 * omitting one that does reintroduces the cursor/sort mismatch this guards against.
 */
export const buildCursorResetKey = (queryInputs: object): string => qs.stringify(queryInputs, { sort: alphabetically });

/**
 * Cursor (keyset) pagination state: a stack of cursors plus the position within it.
 *
 * Cursors returned by the invoice APIs encode the sort they were minted under, and the API
 * rejects a cursor paired with a different sort (`400 Cursor Sort Invalid`). Page size and
 * filters are equally binding — a stale cursor with a new limit returns the wrong slice. The
 * stack is therefore keyed on those inputs via `resetKey` rather than reset imperatively by each
 * handler, so it stays correct even when the inputs change without the caller doing anything:
 * back navigation, or re-entering a route that drops its query string.
 */
export const useCursorPagination = ({ resetKey }: UseCursorPaginationOptions): UseCursorPaginationReturn => {
  const [state, setState] = useStateResetOn<CursorPaginationState>(resetKey, INITIAL_STATE);

  const currentCursor = state.cursors[state.currentIndex];
  const currentPage = state.currentIndex + 1;
  const canGoBack = state.currentIndex > 0;
  const canGoForward = state.currentIndex < state.cursors.length - 1;

  const isNextCursorCached = useCallback(
    (nextCursor: string | null) => {
      const nextIndex = state.currentIndex + 1;
      return nextIndex < state.cursors.length && state.cursors[nextIndex] === nextCursor;
    },
    [state.currentIndex, state.cursors]
  );

  const goToNextPage = useCallback(
    (nextCursor: string | null) => {
      setState((prevState) => {
        // check if we're already at the next position in our cursor stack
        const nextIndex = prevState.currentIndex + 1;

        if (nextIndex < prevState.cursors.length && prevState.cursors[nextIndex] === nextCursor) {
          return {
            ...prevState,
            currentIndex: nextIndex,
          };
        }

        // otherwise, we're adding a new page to our stack
        const newCursors = [...prevState.cursors];

        // only add the next cursor if it's different from the current one and not null
        if (nextCursor && nextCursor !== prevState.cursors[prevState.currentIndex]) {
          newCursors.push(nextCursor);

          return {
            cursors: newCursors,
            currentIndex: prevState.currentIndex + 1,
          };
        }

        return prevState;
      });
    },
    [setState]
  );

  const goToPreviousPage = useCallback(() => {
    setState((prevState) => {
      if (prevState.currentIndex > 0) {
        return {
          ...prevState,
          currentIndex: prevState.currentIndex - 1,
        };
      }

      return prevState;
    });
  }, [setState]);

  return {
    currentCursor,
    currentPage,
    canGoBack,
    canGoForward,
    isNextCursorCached,
    goToNextPage,
    goToPreviousPage,
  };
};
